import { z } from 'zod';

// Letter Data Types
export const LetterPronunciationSchema = z.object({
  pronunciation: z.string(),
  slack: z.number(),
  threshold: z.number(),
  boost: z.number().optional(),
});

export const LetterDataSchema = LetterPronunciationSchema.extend({
  letter: z.string(),
  commonMispronunciations: z.array(LetterPronunciationSchema),
});

// Grader Types
export enum GraderType {
  PHONEME = 'PHONEME',
  WORD = 'WORD',
  SENTENCE = 'SENTENCE',
  LETTER_NAME = 'LETTER_NAME',
}

// Input Types
export const PronunciationGraderParametersInputSchema = z.object({
  graderType: z.nativeEnum(GraderType),
  refText: z.string(),
});

// Parameter Types
export const PhonemeParametersSchema = LetterDataSchema;

export const WordParametersSchema = z.object({
  word: z.string(),
  threshold: z.number(),
});

export const SentenceParametersSchema = z.object({
  sentence: z.string(),
  threshold: z.number(),
});

export const LetterNameParametersSchema = z.object({
  letter: z.string(),
  threshold: z.number(),
});

// Output Types
export const PhonemeParametersOutputSchema = z.object({
  graderType: z.literal(GraderType.PHONEME),
  parameters: PhonemeParametersSchema,
});

export const WordParametersOutputSchema = z.object({
  graderType: z.literal(GraderType.WORD),
  parameters: WordParametersSchema,
});

export const SentenceParametersOutputSchema = z.object({
  graderType: z.literal(GraderType.SENTENCE),
  parameters: SentenceParametersSchema,
});

export const LetterNameParametersOutputSchema = z.object({
  graderType: z.literal(GraderType.LETTER_NAME),
  parameters: LetterNameParametersSchema,
});

export const PronunciationGraderParametersOutputSchema = z.discriminatedUnion('graderType', [
  PhonemeParametersOutputSchema,
  WordParametersOutputSchema,
  SentenceParametersOutputSchema,
  LetterNameParametersOutputSchema,
]);

// Type Inference
export type LetterPronunciation = z.infer<typeof LetterPronunciationSchema>;
export type LetterData = z.infer<typeof LetterDataSchema>;
export type PronunciationGraderParametersInput = z.infer<typeof PronunciationGraderParametersInputSchema>;
export type PhonemeParameters = z.infer<typeof PhonemeParametersSchema>;
export type WordParameters = z.infer<typeof WordParametersSchema>;
export type SentenceParameters = z.infer<typeof SentenceParametersSchema>;
export type LetterNameParameters = z.infer<typeof LetterNameParametersSchema>;
export type PhonemeParametersOutput = z.infer<typeof PhonemeParametersOutputSchema>;
export type WordParametersOutput = z.infer<typeof WordParametersOutputSchema>;
export type SentenceParametersOutput = z.infer<typeof SentenceParametersOutputSchema>;
export type LetterNameParametersOutput = z.infer<typeof LetterNameParametersOutputSchema>;
export type PronunciationGraderParametersOutput = z.infer<typeof PronunciationGraderParametersOutputSchema>;
